/**
 * The Analyze window's top part: which project to scan, and the Scan button.
 *
 * A core with a disk (server, desktop) reads a folder path; the browser build's core has none,
 * so the page asks for a folder (the browser's picker, or a folder input elsewhere), reads it
 * and sends the files. Below, the plugins a scan will use, with a link to the Plugins window.
 */
import { Show, type JSX } from "solid-js";
import { useApp } from "../../app/context";

/** Typing or browsing a folder path the core reads. */
function FolderPath(): JSX.Element {
  const { analysis } = useApp();
  return (
    <>
      <div class="row-inline">
        <input
          class="field mono"
          id="analyze-root"
          placeholder="/path/to/project"
          value={analysis.root()}
          onInput={(e) => analysis.setRoot(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && void analysis.scan()}
        />
        <Show when={analysis.browse}>
          <button type="button" class="btn" id="analyze-browse" onClick={() => void analysis.browse!()}>
            Browse…
          </button>
        </Show>
      </div>
      <div class="btnrow">
        <button type="button" class="btn primary" id="analyze-scan" disabled={analysis.running() || !analysis.root().trim()} onClick={() => void analysis.scan()}>
          {analysis.running() ? "Scanning…" : "Scan"}
        </button>
      </div>
    </>
  );
}

/** Choosing a folder in the page (browser build). */
function FolderInPage(): JSX.Element {
  const { analysis } = useApp();
  let input!: HTMLInputElement;
  /** The browser's picker where there is one, else the folder input. */
  const choose = () => (analysis.canPickInPage ? void analysis.pickAndScan() : input.click());
  return (
    <>
      <p class="small muted">The folder is read by this page, nothing is uploaded. Build output and hidden folders are skipped.</p>
      <input
        type="file"
        id="analyze-folder-input"
        hidden
        // `webkitdirectory` makes the input pick a whole folder (every browser understands it).
        {...{ webkitdirectory: true }}
        multiple
        onChange={(e) => {
          const files = e.currentTarget.files;
          if (files) void analysis.scanFileList(files);
          e.currentTarget.value = "";
        }}
        ref={input}
      />
      <Show when={analysis.picked()}>
        <p class="small mono" data-analyze-picked>
          {analysis.picked()}
        </p>
      </Show>
      <div class="btnrow">
        <button type="button" class="btn primary" id="analyze-choose" disabled={analysis.running()} onClick={choose}>
          {analysis.running() ? "Scanning…" : "Choose folder…"}
        </button>
        <Show when={analysis.picked()}>
          <button type="button" class="btn" id="analyze-scan" disabled={analysis.running()} onClick={() => void analysis.scan()}>
            Scan again
          </button>
        </Show>
      </div>
    </>
  );
}

/** The plugins a scan uses, and the way to manage them. */
function PluginsLine(): JSX.Element {
  const { plugins, wm } = useApp();
  return (
    <p class="small muted" data-analyze-plugins>
      Plugins: {plugins.active().map((p) => p.name).join(", ") || "none turned on"} ·{" "}
      <button type="button" class="linklike link" id="analyze-manage-plugins" onClick={() => wm.open("plugins")}>
        Manage plugins
      </button>
    </p>
  );
}

/** The Analyze window's folder part. */
export function FolderSection(): JSX.Element {
  const { analysis } = useApp();
  return (
    <section class="grp">
      <h3>Project folder</h3>
      <p class="small muted">Your own nodes, notes, comments and positions are kept.</p>
      <PluginsLine />
      <Show when={analysis.readsFolders} fallback={<FolderInPage />}>
        <FolderPath />
      </Show>
      <Show when={analysis.error() && analysis.errorFrom() === "folder"}>
        <p class="small err" data-analyze-error>
          {analysis.error()}
        </p>
      </Show>
    </section>
  );
}
